<template>
  <div class="c-header-mobile-menu-wrapper">
    <div
      class="c-header-mobile-menu-icon-container"
      @click="toggleMenu"
    >
      <div
        class="c-header-mobile-menu-hamburger"
        :class="{ 'c-header-mobile-menu-hamburger_hidden': isOpen }"
      >
        <div class="c-header-mobile-menu-hamburger__line"></div>
        <div class="c-header-mobile-menu-hamburger__line"></div>
        <div class="c-header-mobile-menu-hamburger__line"></div>
      </div>

      <div
        class="c-header-mobile-menu-close"
        :class="{ 'c-header-mobile-menu-close_visible': isOpen }"
      >
        <div class="c-header-mobile-menu-close__line"></div>
        <div class="c-header-mobile-menu-close__line"></div>
      </div>
    </div>

    <div
      class="c-header-mobile-menu"
      :class="{ 'c-header-mobile-menu_open': isOpen }"
    >
      <div class="c-header-mobile-menu__links">
        <div
          v-for="link in links"
          :key="link.id"
          class="c-header-mobile-menu-link"
          :class="{ 'c-header-mobile-menu-link_active': activeSection === link.id }"
          @click="onLinkClick(link.id)"
        >
          <div class="c-header-mobile-menu-link__text">
            {{ link.title }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import { useSectionTracking } from '@/composables/useSectionTracking';

  const route = useRoute();
  const router = useRouter();
  const { links, activeSection, scrollToSection } = useSectionTracking();

  const isOpen = ref(false);

  const isHomePage = computed(() => route.path === '/');

  const toggleMenu = () => {
    isOpen.value = !isOpen.value;

    document.body.style.overflow = isOpen.value ? 'hidden' : 'auto';
  };

  function onLinkClick(linkId: string) {
    if (isHomePage.value) {
      scrollToSection(linkId);
    } else {
      router.push({ path: '/', query: { to: linkId } });
    }

    toggleMenu();
  }
</script>

<style>
  .c-header-mobile-menu {
    position: fixed;
    top: 83px;
    right: 0;
    left: 0;
    bottom: 0;
    background-color: white;
    z-index: 10;
    padding: 12px 24px;
    transition: all 0.3s ease-in-out;
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    visibility: hidden;

    &_open {
      display: block;
      max-height: 100vh;
      opacity: 1;
      visibility: visible;
    }

    &-icon-container {
      position: relative;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      user-select: none;
      outline: none;
      -webkit-tap-highlight-color: transparent;
    }

    &-hamburger {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 5px;
      position: absolute;
      transition: all 0.3s ease;
      opacity: 1;
      transform: scale(1);
      user-select: none;

      &_hidden {
        opacity: 0;
        transform: scale(0.5);
      }

      &__line {
        width: 30px;
        height: 5px;
        background: var(--g-color-orange);
      }
    }

    &-close {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 5px;
      position: absolute;
      opacity: 0;
      transform: scale(0.5);
      transition: all 0.3s ease;

      &_visible {
        opacity: 1;
        transform: scale(1);
      }

      &__line {
        width: 30px;
        height: 5px;
        background: var(--g-color-orange);

        &:first-child {
          transform: rotate(45deg);
          transform-origin: center;
          margin-bottom: -5px;
        }

        &:last-child {
          transform: rotate(-45deg);
          transform-origin: center;
          margin-top: -5px;
        }
      }
    }

    &__links {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    &-link {
      font-size: 24px;
      font-weight: 600;
      position: relative;

      &__text {
        position: relative;
        display: inline-block;
        padding-bottom: 8px;

        &:after {
          content: '';
          display: block;
          width: 100%;
          height: 5px;
          position: absolute;
          bottom: 0;
          top: calc(100% - 5px);
          left: 0;
          right: 0;
          background: var(--g-color-orange);
          transition: all 0.3s ease;
        }
      }

      &_active {
        .c-header-mobile-menu-link__text {
          color: #fff;

          &:after {
            content: '';
            display: block;
            height: calc(100% + 4px);
            position: absolute;
            bottom: 0;
            top: -5px;
            left: -12px;
            right: -12px;
            width: calc(100% + 24px);
            z-index: -1;
            background: var(--g-color-orange);
            transition: all 0.3s ease;
            border-radius: 37.5px;
          }
        }
      }
    }
  }

  @media (--tiny-mobile) {
  }
</style>
